import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { CCard, CCardBody, CCardHeader, CCol, CRow, CButton, CAccordion, CAccordionItem, CAccordionHeader, CAccordionBody, CForm, CFormLabel, CFormInput, CFormTextarea } from '@coreui/react';
import { DataGrid } from '@mui/x-data-grid';
import { Link, useLoaderData, useParams, useNavigate } from 'react-router-dom';
import AxiosInstance from 'src/components/Axios';
import { cilSearch } from '@coreui/icons';
import CIcon from '@coreui/icons-react';
import { FaBell } from 'react-icons/fa';
import { useConvertToLocalTime } from 'src/components/store/useConvertToLocalTime';
import AsyncSelect from 'react-select/async';
import debounce from 'lodash.debounce';
import PermissionsNoPage, { Permissions } from 'src/components/Permissions';

const page = 1;
const urlApi = '/notifications';
const urlMassiveApi = '/massive_notifications';
const urlRouter = '/user-notifications';

export async function LoaderMassiveNotificationsView({ params, request }) {
  if (!params?.pid) {
    return false;
  }

  const searchParams = new URL(request.url).searchParams;
  const userId = searchParams.get('userId');
  const userQuery = userId ? `&users.id=${encodeURIComponent(userId)}` : '';

  const [notifications, massive] = await Promise.all([
    AxiosInstance.get(`${urlApi}?page=${page}&massiveNotification.id=${encodeURIComponent(params.pid)}${userQuery}`),
    AxiosInstance.get(`${urlMassiveApi}/${encodeURIComponent(params.pid)}`),
  ]);

  return { notifications, massive };
}

const MassiveNotificationsView = ({ user_data }) => {
  // if (!Permissions(user_data, 'MassNotifications', 'VIEW_MASSNOTIFICATIONS')) {
  //   return <PermissionsNoPage />;
  // }
  const { notifications, massive } = useLoaderData();
  const { pid } = useParams();
  const navigate = useNavigate();

  const [rows, setRows] = useState(notifications?.data?.['hydra:member'] || []);
  const [rowCountState, setRowCountState] = useState(notifications?.data?.['hydra:totalItems'] || 0);
  const [massiveData, setMassiveData] = useState(massive?.data || null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);

  useEffect(() => {
    setRows(notifications?.data?.['hydra:member'] || []);
    setRowCountState(notifications?.data?.['hydra:totalItems'] || 0);
  }, [notifications]);

  useEffect(() => {
    setMassiveData(massive?.data || null);
  }, [massive]);

  const getRowsTable = useCallback((p = 1, userIdParam) => {
    const userId = userIdParam !== undefined ? userIdParam : selectedUserId;
    const userQuery = userId ? `&users.id=${encodeURIComponent(userId)}` : '';

    AxiosInstance.get(`${urlApi}?page=${p}&massiveNotification.id=${encodeURIComponent(pid)}${userQuery}`).then((response) => {
      setRows(response.data['hydra:member']);
      setRowCountState(response.data['hydra:totalItems']);
    });
  }, [pid, selectedUserId]);

  const fetchUserOptions = useCallback(async (inputValue) => {
    const query = inputValue.trim();
    if (!query || query.length < 3) {
      return [];
    }

    const response = await AxiosInstance.get(`/user/search?s=${encodeURIComponent(query)}`);
    return response.data.map((user) => {
      const name = user.firstName ? ` (${user.firstName})` : '';
      return {
        label: `${user.username}${name}`,
        value: user.id,
      };
    });
  }, []);

  const debouncedLoadUserOptions = useMemo(() => debounce((inputValue, resolve, reject) => {
    fetchUserOptions(inputValue).then(resolve).catch(reject);
  }, 300), [fetchUserOptions]);

  useEffect(() => {
    return () => debouncedLoadUserOptions.cancel();
  }, [debouncedLoadUserOptions]);

  const loadUserOptions = useCallback((inputValue) => {
    return new Promise((resolve, reject) => debouncedLoadUserOptions(inputValue, resolve, reject));
  }, [debouncedLoadUserOptions]);

  const handleUserChange = (option) => {
    const userId = option ? option.value : null;
    setSelectedUser(option);
    setSelectedUserId(userId);
    getRowsTable(1, userId);
  };

  const handlePageChange = (p) => {
    getRowsTable(p + 1);
  };

  const handleClearFilters = () => {
    setSelectedUser(null);
    setSelectedUserId(null);
    getRowsTable(1, null);
  };

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const columns = useMemo(() => [
    { field: 'id', headerName: 'ID', width: 90 },
    {
      field: 'username',
      headerName: 'username',
      width: 200,
      renderCell: (params) => (
        <Link to={`${urlRouter}/view/${params.row.id}`} style={{ textDecoration: 'none', color: 'rgba(0, 0, 0, 0.87)' }}>
          {params.row.users.username}
        </Link>
      ),
    },
    {
      field: 'firstName',
      headerName: "Ім'я",
      width: 280,
      renderCell: (params) => (
        <Link to={`${urlRouter}/view/${params.row.id}`} style={{ textDecoration: 'none', color: 'rgba(0, 0, 0, 0.87)' }}>
          {params.row.users.firstName}
        </Link>
      ),
    },
    { field: 'title', headerName: 'Назва', width: 240 },
    { field: 'status', headerName: 'Статус', width: 120 },
    {
      field: 'dateEntered',
      headerName: 'Дата створення',
      width: 200,
      renderCell: (params) => <span>{useConvertToLocalTime(params.row.dateEntered)}</span>,
    },
    { field: 'error', headerName: 'Помилка', width: 200 },
    {
      field: 'action',
      headerName: 'Переглянути',
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      width: 110,
      renderCell: (params) => (
        <Link
          to={`${urlRouter}/view/${params.row.id}`}
          className='cutom-view-icon '
        >
          <CIcon icon={cilSearch} customClassName="nav-icon" height={18} />
        </Link>
      ),
    },
  ], []);

  return (
    <CRow>
      <CCol xs={12}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center">
            <FaBell className="nav-icon me-2" />
            <h5 className="mb-0">Сповіщення масової розсилки</h5>
          </div>
          <CButton color="secondary" variant="outline" onClick={handleBack}>
            Назад
          </CButton>
        </div>
        <CCard className="mb-4">
          <CCardHeader>
            <strong>Масове сповіщення ID: {pid}</strong>
          </CCardHeader>
          <CCardBody>
            {massiveData && (
              <CForm className="mb-4">
                <CRow className="g-3">
                  <CCol md={6}>
                    <CFormLabel>Назва</CFormLabel>
                    <CFormInput value={massiveData.name || ''} disabled readOnly />
                  </CCol>
                  <CCol md={6}>
                    <CFormLabel>Тема</CFormLabel>
                    <CFormInput value={massiveData.title || ''} disabled readOnly />
                  </CCol>
                  <CCol md={12}>
                    <CFormLabel>Текст</CFormLabel>
                    <CFormTextarea value={massiveData.body || ''} disabled readOnly rows={3} />
                  </CCol>
                  <CCol md={4}>
                    <CFormLabel>Статус</CFormLabel>
                    <CFormInput value={massiveData.status || ''} disabled readOnly />
                  </CCol>
                  <CCol md={4}>
                    <CFormLabel>Створив користувач</CFormLabel>
                    <CFormInput value={massiveData.userCreated?.username || ''} disabled readOnly />
                  </CCol>
                </CRow>
              </CForm>
            )}
            <CAccordion flush>
              <CAccordionItem>
                <CAccordionHeader><strong>Фільтр</strong></CAccordionHeader>
                <CAccordionBody>
                  <CRow className="my-3 mx-2">
                    <CCol md={8}>
                      <AsyncSelect
                        cacheOptions
                        defaultOptions={false}
                        loadOptions={loadUserOptions}
                        value={selectedUser}
                        onChange={handleUserChange}
                        isClearable
                        placeholder="Пошук користувача"
                      />
                    </CCol>
                    <CCol md={4}>
                      <CButton color="secondary" variant="outline" onClick={handleClearFilters}>
                        Очистити фільтри
                      </CButton>
                    </CCol>
                  </CRow>
                </CAccordionBody>
              </CAccordionItem>
            </CAccordion>
            <div style={{ height: 600, width: '100%' }}>
              <DataGrid
                rows={rows}
                columns={columns}
                pageSize={30}
                rowsPerPageOptions={[30]}
                pagination
                rowCount={rowCountState}
                paginationMode="server"
                onPageChange={handlePageChange}
              />
            </div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
  );
};

export default MassiveNotificationsView;

